/**
 * Learning Content Tab — תוכן לימודי
 *
 * Admin editor for ONE track's content: the topic lessons and, on tracks that
 * have an exam, the question bank. Defaults to the student track, so the
 * original פורטל תלמידים tab is unchanged.
 * Content is editable without a redeploy — the seed script only
 * fills in what's missing, so nothing typed here is ever overwritten by a deploy.
 *
 * The lesson editor deliberately offers h3/h4 but not h1/h2: the student page owns
 * those levels, and the server sanitizer strips them anyway to keep the heading
 * hierarchy valid for screen readers.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AlertCircle,
  Bold,
  Check,
  DownloadCloud,
  Heading,
  Italic,
  List,
  ListOrdered,
  Plus,
  Power,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  X,
} from 'lucide-react'
import { useAdminStore } from '../../store/adminStore'
import { TRACKS, type TrackKey } from '../../config/tracks'
import type { ExamQuestionAdmin, LearningTopicAdmin } from '../../types'

function ToolbarButton({
  title,
  onClick,
  children,
}: {
  title: string
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="rounded p-1.5 text-gray-700 hover:bg-gray-200"
    >
      {children}
    </button>
  )
}

const EMPTY_QUESTION: Partial<ExamQuestionAdmin> = {
  questionText: '',
  options: [{ text: '' }, { text: '' }, { text: '' }, { text: '' }],
  correctIndex: 0,
  explanation: '',
  difficulty: 'medium',
  order: 0,
  isActive: true,
}

interface LearningContentTabProps {
  /** Which track's content to edit. Defaults to the school programme. */
  track?: TrackKey
}

export default function LearningContentTab({ track = 'student' }: LearningContentTabProps) {
  // Only the school programme has an exam, so the question bank is hidden
  // elsewhere rather than shown as an empty, confusing section.
  const hasExam = TRACKS[track].hasExam
  const {
    learningTopics,
    learningTopicsLoading,
    learningTopicsError,
    examQuestions,
    examQuestionsError,
    fetchLearningTopics,
    seedLearningContent: seedLearningContentAction,
    updateLearningTopic,
    toggleLearningTopic,
    fetchExamQuestions,
    createExamQuestion,
    updateExamQuestion,
    toggleExamQuestion,
    deleteExamQuestion,
  } = useAdminStore()

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const [draft, setDraft] = useState<LearningTopicAdmin | null>(null)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState('')
  const [localError, setLocalError] = useState('')
  const [questionDraft, setQuestionDraft] = useState<Partial<ExamQuestionAdmin> | null>(null)
  const [seeding, setSeeding] = useState(false)
  const editorRef = useRef<HTMLDivElement>(null)

  /**
   * Installs the shipped curriculum. `force` restores the defaults over existing
   * content, so it asks first — an admin who has spent time editing lessons must
   * not lose them to a stray click.
   */
  const handleSeed = async (force: boolean) => {
    if (
      force &&
      !window.confirm(
        'לשחזר את תוכן ברירת המחדל?\n\nכל עריכה שביצעת בשיעורים ובשאלות המובנים תידרס ותוחלף בגרסה המקורית.'
      )
    ) {
      return
    }
    setSeeding(true)
    setLocalError('')
    try {
      const message = await seedLearningContentAction(force, track)
      setSuccess(message)
    } catch {
      /* store surfaces the error */
    } finally {
      setSeeding(false)
    }
  }

  useEffect(() => {
    void fetchLearningTopics(track)
    if (hasExam) void fetchExamQuestions()
  }, [fetchLearningTopics, fetchExamQuestions, track, hasExam])

  const selected = learningTopics.find((t) => t.slug === selectedSlug) || null

  // Load the picked topic into an editable draft and seed the contentEditable.
  const selectTopic = useCallback((topic: LearningTopicAdmin) => {
    setSelectedSlug(topic.slug)
    setDraft({ ...topic })
    setSuccess('')
    setLocalError('')
    setQuestionDraft(null)
    requestAnimationFrame(() => {
      if (editorRef.current) editorRef.current.innerHTML = topic.lessonHtml || ''
    })
  }, [])

  const exec = (command: string, value?: string) => {
    editorRef.current?.focus()
    document.execCommand(command, false, value)
    if (editorRef.current && draft) {
      setDraft({ ...draft, lessonHtml: editorRef.current.innerHTML })
    }
    setSuccess('')
  }

  const handleSaveTopic = async () => {
    if (!draft || !selected) return
    if (!draft.title.trim()) {
      setLocalError('כותרת הנושא חסרה')
      return
    }
    setSaving(true)
    setLocalError('')
    try {
      await updateLearningTopic(selected._id, {
        title: draft.title,
        subtitle: draft.subtitle,
        icon: draft.icon,
        order: draft.order,
        lessonHtml: editorRef.current?.innerHTML ?? draft.lessonHtml,
        keyTerms: draft.keyTerms,
        suggestedQuestions: draft.suggestedQuestions,
        chatScope: draft.chatScope,
        isActive: draft.isActive,
      }, track)
      setSuccess('הנושא נשמר. המשתמשים יראו את הגרסה החדשה מיד.')
    } catch {
      /* store surfaces the error */
    } finally {
      setSaving(false)
    }
  }

  const handleSaveQuestion = async () => {
    if (!questionDraft) return
    const options = (questionDraft.options || []).filter((o) => o.text.trim())
    if (!questionDraft.questionText?.trim()) {
      setLocalError('נוסח השאלה חסר')
      return
    }
    if (options.length < 2) {
      setLocalError('נדרשות לפחות שתי תשובות')
      return
    }
    if ((questionDraft.correctIndex ?? 0) >= options.length) {
      setLocalError('יש לסמן איזו תשובה נכונה')
      return
    }
    setSaving(true)
    setLocalError('')
    try {
      const payload = {
        questionText: questionDraft.questionText,
        options,
        correctIndex: questionDraft.correctIndex ?? 0,
        explanation: questionDraft.explanation || '',
        topicSlug: questionDraft.topicSlug || selectedSlug || '',
        difficulty: questionDraft.difficulty || 'medium',
        order: questionDraft.order ?? 0,
        isActive: questionDraft.isActive ?? true,
      }
      if (questionDraft._id) {
        await updateExamQuestion(questionDraft._id, payload)
      } else {
        await createExamQuestion(payload)
      }
      setQuestionDraft(null)
      setSuccess('השאלה נשמרה')
    } catch {
      /* store surfaces the error */
    } finally {
      setSaving(false)
    }
  }

  const topicQuestions = examQuestions.filter((q) => q.topicSlug === selectedSlug)
  const error = localError || learningTopicsError || examQuestionsError

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">תוכן לימודי</h2>
          <p className="mt-1 text-sm text-gray-600">
            עריכת השיעורים ומאגר שאלות המבחן של מסלול הלמידה לתלמידים. השינויים נכנסים לתוקף
            מיד, בלי צורך בהעלאת גרסה.
          </p>
        </div>
        {learningTopics.length > 0 && (
          <button
            type="button"
            onClick={() => handleSeed(true)}
            disabled={seeding}
            className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {seeding ? <RefreshCw className="animate-spin" size={14} /> : <RotateCcw size={14} />}
            שחזור תוכן ברירת המחדל
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700" role="alert">
          <AlertCircle size={16} aria-hidden="true" /> {error}
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
          <Check size={16} aria-hidden="true" /> {success}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
        {/* Topic list */}
        <div>
          <h3 className="mb-2 text-sm font-semibold text-gray-900">נושאים</h3>
          {learningTopicsLoading && learningTopics.length === 0 && (
            <p className="text-sm text-gray-600">טוען...</p>
          )}
          {!learningTopicsLoading && learningTopics.length === 0 && (
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-3">
              <p className="text-xs text-gray-800">
                עדיין אין תוכן לימודי. אפשר לטעון את ששת הנושאים ומאגר השאלות המובנים,
                ואז לערוך אותם כאן.
              </p>
              <button
                type="button"
                onClick={() => handleSeed(false)}
                disabled={seeding}
                className="mt-2 flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {seeding ? <RefreshCw className="animate-spin" size={14} /> : <DownloadCloud size={14} />}
                טעינת תוכן ברירת המחדל
              </button>
            </div>
          )}
          <ul className="space-y-1">
            {learningTopics.map((topic) => (
              <li key={topic._id}>
                <button
                  type="button"
                  onClick={() => selectTopic(topic)}
                  aria-current={selectedSlug === topic.slug ? 'true' : undefined}
                  className={`w-full rounded-lg border px-3 py-2 text-right text-sm ${
                    selectedSlug === topic.slug
                      ? 'border-primary-400 bg-primary-50 font-semibold text-primary-900'
                      : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <span className="block">{topic.title}</span>
                  <span className="mt-0.5 block text-xs font-normal text-gray-600">
                    {topic.isExam ? 'מבחן' : `${topic.questionCount ?? 0} שאלות`}
                    {!topic.isActive && ' · מושבת'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Editor */}
        <div>
          {!draft && (
            <p className="rounded-lg bg-gray-50 p-6 text-center text-sm text-gray-700">
              בחר/י נושא מהרשימה כדי לערוך אותו.
            </p>
          )}

          {draft && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-base font-bold text-gray-900">{draft.title}</h3>
                <button
                  type="button"
                  onClick={() => selected && toggleLearningTopic(selected._id, track)}
                  className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                >
                  <Power size={14} aria-hidden="true" />
                  {draft.isActive ? 'השבת נושא' : 'הפעל נושא'}
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="topic-title" className="mb-1 block text-sm font-medium text-gray-700">
                    כותרת
                  </label>
                  <input
                    id="topic-title"
                    type="text"
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="topic-subtitle" className="mb-1 block text-sm font-medium text-gray-700">
                    תת-כותרת (על הריבוע)
                  </label>
                  <input
                    id="topic-subtitle"
                    type="text"
                    value={draft.subtitle}
                    onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              {!draft.isExam && (
                <>
                  <div>
                    <span className="mb-1 block text-sm font-medium text-gray-700">תוכן השיעור</span>
                    <div className="flex flex-wrap gap-1 rounded-t-lg border border-b-0 border-gray-300 bg-gray-50 px-2 py-1">
                      <ToolbarButton title="מודגש" onClick={() => exec('bold')}>
                        <Bold size={16} />
                      </ToolbarButton>
                      <ToolbarButton title="נטוי" onClick={() => exec('italic')}>
                        <Italic size={16} />
                      </ToolbarButton>
                      <ToolbarButton title="כותרת משנה" onClick={() => exec('formatBlock', 'h3')}>
                        <Heading size={16} />
                      </ToolbarButton>
                      <ToolbarButton title="פסקה" onClick={() => exec('formatBlock', 'p')}>
                        <span className="px-1 text-xs font-semibold">טקסט</span>
                      </ToolbarButton>
                      <ToolbarButton title="רשימת תבליטים" onClick={() => exec('insertUnorderedList')}>
                        <List size={16} />
                      </ToolbarButton>
                      <ToolbarButton title="רשימה ממוספרת" onClick={() => exec('insertOrderedList')}>
                        <ListOrdered size={16} />
                      </ToolbarButton>
                    </div>
                    <div
                      ref={editorRef}
                      role="textbox"
                      aria-multiline="true"
                      aria-label="תוכן השיעור"
                      contentEditable
                      suppressContentEditableWarning
                      onInput={() =>
                        editorRef.current && setDraft({ ...draft, lessonHtml: editorRef.current.innerHTML })
                      }
                      className="lesson-content max-h-[420px] min-h-[260px] overflow-y-auto rounded-b-lg border border-gray-300 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <p className="mt-1 text-xs text-gray-600">
                      כותרות בשיעור מתחילות מרמה 3 — הכותרת הראשית שייכת לעמוד עצמו (נדרש לנגישות).
                    </p>
                  </div>

                  <div>
                    <label htmlFor="topic-scope" className="mb-1 block text-sm font-medium text-gray-700">
                      הקשר לצ'אט (מה המדריך יתמקד בו)
                    </label>
                    <textarea
                      id="topic-scope"
                      value={draft.chatScope}
                      onChange={(e) => setDraft({ ...draft, chatScope: e.target.value })}
                      rows={3}
                      maxLength={1000}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-600">{draft.chatScope.length} / 1000 תווים</p>
                  </div>

                  <div>
                    <label htmlFor="topic-suggestions" className="mb-1 block text-sm font-medium text-gray-700">
                      שאלות מוצעות (שורה לכל שאלה)
                    </label>
                    <textarea
                      id="topic-suggestions"
                      value={draft.suggestedQuestions.join('\n')}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          suggestedQuestions: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean).slice(0, 8),
                        })
                      }
                      rows={4}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="topic-terms" className="mb-1 block text-sm font-medium text-gray-700">
                      מושגי מפתח (שורה לכל מושג, בפורמט: מושג | הגדרה)
                    </label>
                    <textarea
                      id="topic-terms"
                      value={draft.keyTerms.map((k) => `${k.term} | ${k.definition}`).join('\n')}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          keyTerms: e.target.value
                            .split('\n')
                            .map((line) => {
                              const [term, ...rest] = line.split('|')
                              return { term: (term || '').trim(), definition: rest.join('|').trim() }
                            })
                            .filter((k) => k.term && k.definition)
                            .slice(0, 30),
                        })
                      }
                      rows={5}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </>
              )}

              <button
                type="button"
                onClick={handleSaveTopic}
                disabled={saving}
                className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? <RefreshCw className="animate-spin" size={16} /> : <Save size={16} />}
                שמירת הנושא
              </button>

              {/* Question bank — only tracks with an exam have one, and an empty
                  "שאלות מבחן" section on the others reads as a bug, not a feature. */}
              {hasExam && (
              <section className="mt-8 border-t border-gray-200 pt-6" aria-labelledby="question-bank-heading">
                <div className="flex items-center justify-between">
                  <h3 id="question-bank-heading" className="text-base font-bold text-gray-900">
                    שאלות מבחן בנושא ({topicQuestions.length})
                  </h3>
                  <button
                    type="button"
                    onClick={() =>
                      setQuestionDraft({ ...EMPTY_QUESTION, topicSlug: selectedSlug || '' })
                    }
                    className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    <Plus size={14} aria-hidden="true" /> שאלה חדשה
                  </button>
                </div>

                {questionDraft && (
                  <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50/40 p-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-900">
                        {questionDraft._id ? 'עריכת שאלה' : 'שאלה חדשה'}
                      </h4>
                      <button
                        type="button"
                        onClick={() => setQuestionDraft(null)}
                        aria-label="ביטול"
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <label htmlFor="q-text" className="mt-3 mb-1 block text-sm font-medium text-gray-700">
                      נוסח השאלה
                    </label>
                    <textarea
                      id="q-text"
                      value={questionDraft.questionText || ''}
                      onChange={(e) => setQuestionDraft({ ...questionDraft, questionText: e.target.value })}
                      rows={2}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />

                    <fieldset className="mt-3">
                      <legend className="mb-1 text-sm font-medium text-gray-700">
                        תשובות (יש לסמן את הנכונה)
                      </legend>
                      {(questionDraft.options || []).map((opt, i) => (
                        <div key={i} className="mb-2 flex items-center gap-2">
                          <input
                            type="radio"
                            name="correct-option"
                            id={`correct-${i}`}
                            checked={questionDraft.correctIndex === i}
                            onChange={() => setQuestionDraft({ ...questionDraft, correctIndex: i })}
                            className="h-4 w-4 accent-primary-600"
                          />
                          <label htmlFor={`correct-${i}`} className="sr-only">
                            סמן תשובה {i + 1} כנכונה
                          </label>
                          <input
                            type="text"
                            aria-label={`טקסט תשובה ${i + 1}`}
                            value={opt.text}
                            onChange={(e) => {
                              const options = [...(questionDraft.options || [])]
                              options[i] = { text: e.target.value }
                              setQuestionDraft({ ...questionDraft, options })
                            }}
                            className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                          />
                        </div>
                      ))}
                    </fieldset>

                    <label htmlFor="q-explanation" className="mt-2 mb-1 block text-sm font-medium text-gray-700">
                      הסבר (מוצג לתלמיד אחרי ההגשה)
                    </label>
                    <textarea
                      id="q-explanation"
                      value={questionDraft.explanation || ''}
                      onChange={(e) => setQuestionDraft({ ...questionDraft, explanation: e.target.value })}
                      rows={2}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />

                    <button
                      type="button"
                      onClick={handleSaveQuestion}
                      disabled={saving}
                      className="mt-3 flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                      <Save size={16} /> שמירת השאלה
                    </button>
                  </div>
                )}

                <ul className="mt-4 space-y-2">
                  {topicQuestions.map((q) => (
                    <li
                      key={q._id}
                      className="flex items-start justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3"
                    >
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">{q.questionText}</p>
                        <p className="mt-1 text-xs text-gray-600">
                          תשובה נכונה: {q.options[q.correctIndex]?.text}
                          {!q.isActive && ' · מושבתת'}
                          {q.timesServed > 0 &&
                            ` · הוצגה ${q.timesServed} פעמים, ${Math.round((q.timesCorrect / q.timesServed) * 100)}% נכון`}
                        </p>
                      </div>
                      <div className="flex flex-shrink-0 gap-1">
                        <button
                          type="button"
                          onClick={() => setQuestionDraft({ ...q })}
                          aria-label={`עריכת השאלה: ${q.questionText}`}
                          className="rounded p-1.5 text-gray-600 hover:bg-gray-100"
                        >
                          <Save size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleExamQuestion(q._id)}
                          aria-label={`${q.isActive ? 'השבתת' : 'הפעלת'} השאלה: ${q.questionText}`}
                          className="rounded p-1.5 text-gray-600 hover:bg-gray-100"
                        >
                          <Power size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('למחוק את השאלה?')) void deleteExamQuestion(q._id)
                          }}
                          aria-label={`מחיקת השאלה: ${q.questionText}`}
                          className="rounded p-1.5 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
